import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Field, Form, FormProps } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  IMenuStructureRequest,
  ListRequestOfBaseFilter,
  ListRequestOfMenuStructureFilter,
  ListResponseOfAdminMenuStructureType,
  ListResponseOfMenuItem,
  ListResponseOfMenuStructure,
  MenuStructure,
} from "services/KnowfullyAdminClient";
import { CommonFormProps } from "types/common-types";

type MenuStructureFormProps = {
  menuId: string;
  menuStructureId?: string | undefined;
} & CommonFormProps<MenuStructure, IMenuStructureRequest> &
  FormProps<IMenuStructureRequest>;

const MenuStructureForm = ({
  menuId,
  menuStructureId,
  entity: menuStructure,
  submitLabel,
  onSubmit,
  onCancel,
}: MenuStructureFormProps) => {
  const { formatMessage: t } = useIntl();
  const { menuService } = useGlobalContext();
  const [isUpdate, setIsUpdate] = useState(false);

  useEffect(() => {
    setIsUpdate(menuStructureId ? true : false);
  }, [menuStructureId]);

  const init = useMemo(
    () => ({
      ...pick(menuStructure ?? {}, [
        "id",
        "menuId",
        "menuItemId",
        "menuItem",
        "menuStructureType",
        "menuStructureTypeId",
        "parentMenuStructureId",
        "name",
        "toolTip",
        "sequence",
      ]),
    }),
    [menuStructure]
  );

  const fetchedMenuItems = useQuery<ListResponseOfMenuItem>("menuItems", () =>
    menuService.getMenuItems(
      new ListRequestOfBaseFilter({
        sortField: "name",
      })
    )
  );

  const fetchedMenuStructures = useQuery<ListResponseOfMenuStructure>(
    ["parentMenuStructures", menuId, menuStructureId],
    () =>
      menuService.getParentMenuStructureOptions(
        menuId,
        menuStructureId,
        new ListRequestOfMenuStructureFilter({
          sortField: "name",
        })
      )
  );

  const fetchedMenuStructureTypes = useQuery<ListResponseOfAdminMenuStructureType>(
    "menuStructureTypes",
    () =>
      menuService.getMenuStructureTypes(
        new ListRequestOfBaseFilter({
          sortField: "name",
        })
      )
  );

  return useMemo(() => {
    const requiredField = (value: string | undefined) =>
      value ? undefined : "Required";
    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="menuItemId" required validate={requiredField}>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedMenuItems}
                          labelField="name"
                          label={t({ id: "menuItemsLabel" })}
                          required={true}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field
                  name="menuStructureTypeId"
                  required
                  validate={requiredField}
                >
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedMenuStructureTypes}
                          labelField="name"
                          label={t({ id: "menuStructureTypeLabel" })}
                          required={true}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="parentMenuStructureId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedMenuStructures}
                          labelField="name"
                          label={t({ id: "parentMenuStructureLabel" })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>
                <Field name="name">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="nameText"
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="toolTip">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "toolTipLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="toolTipText"
                      />
                    </Grid>
                  )}
                </Field>
              </Grid>
            </DialogContent>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button disabled={submitting} onClick={onCancel} data-cy="cancelButton">
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting} data-cy="saveButton">
                {t(
                  { id: "menuStructureCreateButtonLabel" },
                  { prefix: isUpdate ? "Update" : "Create" }
                )}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    fetchedMenuItems,
    fetchedMenuStructureTypes,
    fetchedMenuStructures,
    init,
    onCancel,
    onSubmit,
    t,
    isUpdate,
  ]);
};

export default MenuStructureForm;
